import { useState } from 'react';

import { faCircleInfo } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Typography } from 'antd';
import classNames from 'classnames';
import Router from 'next/router';

import { AccountTierType } from '@evenfinancial/finance-client';
import type { SaasPlan } from '@evenfinancial/finance-client';

import ConfirmationModal from '@/components/confirmation-modal';

import { SubscriptionPlanCard } from './SubscriptionPlanCard';
import type { SubscriptionPlanType } from './SubscriptionPlanCard/types';

const { Text } = Typography;

const SUBSCRIPTION_PLANS_COPY = {
  integrationFeesHeader: 'Integration Fees',
  submitForReviewMessage:
    'You must purchase a plan before submitting your integration for review.',
  upgradeModalMessage:
    'Upgrade will cancel your existing subscription once you purchase a new plan. If you had leftover amount from existing plan that was prepaid, it will be deducted from your total when you purchase the new plan.',
  upgradeModalTitle: 'Upgrade to growth',
} as const;

const SUBSCRIPTION_PLAN_FOOTNOTES = {
  businessModels:
    'Other business models and terms applicable per financial product vertical and tier',
  complianceApproval:
    "Each integration must receive Compliance approval prior to going live. Any requested changes must be implemented and then reviewed by Even's Compliance team. Should you not wish to continue or make the requested changes, your subscription will terminate. The Monthly Platform Fee is nonrefundable.",
  implementationFee: 'Implementation fee applies per-integration',
  seatFee: 'Fee applies for additional seats',
} as const;

export type SubscriptionPlansUIProps = {
  currentPlan?: SaasPlan;
  implementationFeeWaived: boolean;
  isSubmitForReview: boolean;
  plans: SubscriptionPlanType[];
};

export const SubscriptionPlansUI: React.FC<SubscriptionPlansUIProps> = ({
  currentPlan,
  implementationFeeWaived,
  isSubmitForReview,
  plans,
}) => {
  const [growthPlanModalVisible, setGrowthPlanModalVisible] = useState(false);

  return (
    <>
      <ConfirmationModal
        cancelText="Close"
        okText="Next"
        title={SUBSCRIPTION_PLANS_COPY.upgradeModalTitle}
        visible={growthPlanModalVisible}
        onCancel={() => setGrowthPlanModalVisible(false)}
        onOk={() => {
          Router.push(`/purchase-plan?plan=${AccountTierType.Growth}`);
        }}
      >
        <Text>{SUBSCRIPTION_PLANS_COPY.upgradeModalMessage}</Text>
      </ConfirmationModal>
      {isSubmitForReview && (
        <div className="subscription-text">
          <Text className="subscription-text-content">
            <FontAwesomeIcon
              icon={faCircleInfo}
              style={{ height: 16, width: 16 }}
            />
            {SUBSCRIPTION_PLANS_COPY.submitForReviewMessage}
          </Text>
        </div>
      )}
      <div className="subscription-plans">
        {plans.map((plan) => {
          const isUpgradeToGrowthPath =
            plan.id === AccountTierType.Growth &&
            currentPlan?.tier === AccountTierType.Starter;

          const planWithHandler = isUpgradeToGrowthPath
            ? {
                ...plan,
                actionButtonTitle: 'Upgrade',
                onActionButtonClick: () => setGrowthPlanModalVisible(true),
              }
            : plan;

          return (
            <SubscriptionPlanCard
              key={`${plan.id}-subscription-plan-card`}
              currentPlan={currentPlan}
              plan={planWithHandler}
            />
          );
        })}
      </div>
      <div className="integration-options">
        <div className="integration-options-header">
          {SUBSCRIPTION_PLANS_COPY.integrationFeesHeader}
        </div>
        <div className="integration-options-body">
          {plans.map(({ id, optionIcon: Icon, optionName, optionFee }) => (
            <div
              key={`${id}-integration-option`}
              className="integration-option"
            >
              <p
                className={classNames({
                  waived: implementationFeeWaived && optionName !== 'Native',
                })}
              >
                {optionFee}
              </p>
              <div>
                {Icon && <Icon />}
                <span>
                  {optionName}
                  <sup>4</sup>
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="subscription-plans-footnote">
        <ol>
          <li>{SUBSCRIPTION_PLAN_FOOTNOTES.businessModels}</li>
          <li>{SUBSCRIPTION_PLAN_FOOTNOTES.implementationFee}</li>
          <li>{SUBSCRIPTION_PLAN_FOOTNOTES.seatFee}</li>
          <li>{SUBSCRIPTION_PLAN_FOOTNOTES.complianceApproval}</li>
        </ol>
      </div>
    </>
  );
};
